import type { SuggestResult } from '#layers/base/server/utils/search/domain'
import type { SearchSuggestion, SuggestionType } from '~/types'

export interface SuggestionDto {
  suggestions: SearchSuggestion[]
}

interface SearchHistoryResponse {
  queries: string[]
}

/** Maps the hybrid endpoints' plain query strings onto the shared suggestion shape. */
function toSuggestionDto(queries: string[]): SuggestionDto {
  return { suggestions: queries.map(q => ({ label: q, value: q, type: 'product' as SuggestionType })) }
}

/** Flag-dispatched history/autocomplete access; store stays state-only. Drop *Legacy when hybrid is sole path. */
export function useSearchAutocompleteAndHistory() {
  // --- Legacy search-history/autocomplete ---

  async function getSearchHistoryLegacy(): Promise<SuggestionDto> {
    const { baseURL } = useRuntimeConfig().app
    return await $fetch<SuggestionDto>('/api/searchHistory', { baseURL })
  }

  async function autocompleteLegacy(searchQuery: string): Promise<SuggestionDto> {
    const { baseURL } = useRuntimeConfig().app
    return await $fetch<SuggestionDto>(`/api/v2/findSuggestions?q=${searchQuery}`, {
      baseURL,
      onResponseError({ response }) {
        if (response.status === 401) void useUserStore().reloadAuth()
      },
    })
  }

  // --- Hybrid search-history/autocomplete (SHP-3747 / SHP-3748) ---

  async function getSearchHistoryHybrid(): Promise<SuggestionDto> {
    const { baseURL } = useRuntimeConfig().app
    const userStore = useUserStore()
    const { withAuthRetry } = useWithAuthRetry()
    const cHandle = userStore.customerHandle
    if (!cHandle || !userStore.accessToken)
      return { suggestions: [] }

    const responseData = await withAuthRetry(() => $fetch<SearchHistoryResponse>('/api/intern/search-history', {
      baseURL,
      query: { cHandle },
      headers: { Authorization: `Bearer ${userStore.accessToken}` },
    }))

    return toSuggestionDto(responseData.queries)
  }

  async function autocompleteHybrid(searchQuery: string): Promise<SuggestionDto> {
    const { baseURL } = useRuntimeConfig().app
    const userStore = useUserStore()
    const { withAuthRetry } = useWithAuthRetry()

    const responseData = await withAuthRetry(() => $fetch<SuggestResult>('/api/intern/search/suggest', {
      method: 'POST',
      baseURL,
      headers: { Authorization: `Bearer ${userStore.accessToken}` },
      body: { q: searchQuery, limit: 8 },
    }))

    return toSuggestionDto(responseData.suggestions)
  }

  // --- Feature-flag dispatch ---

  function getSearchHistory(): Promise<SuggestionDto> {
    return useIsHybridSearch().value ? getSearchHistoryHybrid() : getSearchHistoryLegacy()
  }

  function autocomplete(searchQuery: string): Promise<SuggestionDto> {
    return useIsHybridSearch().value ? autocompleteHybrid(searchQuery) : autocompleteLegacy(searchQuery)
  }

  return {
    getSearchHistory,
    autocomplete,
  }
}
